import { useMemo } from "react";
import { MapPin, Camera, Tag } from "lucide-react";
import { jobPhotoSrc } from "../../lib/api";

const PHASE_LABEL = { before: "Before", during: "During", after: "After" };

function dayLabel(iso) {
  const d = iso?.slice(0, 10);
  const today = new Date().toLocaleDateString("en-CA");
  const yest = new Date(Date.now() - 86400000).toLocaleDateString("en-CA");
  if (d === today) return "Today";
  if (d === yest) return "Yesterday";
  return d;
}

function timeOf(iso) {
  try {
    return new Date(iso).toLocaleTimeString("en-CA", { hour: "numeric", minute: "2-digit" });
  } catch { return ""; }
}

export function SiteFeed({ jobs, onOpenPhoto }) {
  const entries = useMemo(() => {
    const rows = jobs.filter((j) => j.uploaded_by && j.uploaded_by !== "seed");
    rows.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    const groups = [];
    for (const j of rows) {
      const label = dayLabel(j.created_at);
      const last = groups[groups.length - 1];
      if (last && last.label === label) last.items.push(j);
      else groups.push({ label, items: [j] });
    }
    return groups;
  }, [jobs]);

  return (
    <section className="bg-white border-2 border-[#2C3539]/10 p-5 md:p-8" data-testid="live-site-feed">
      <div className="font-display text-2xl md:text-3xl uppercase text-[#23282B]">Site Feed</div>
      <p className="mt-1 text-sm text-[#23282B]/60">Every photo and progress note from the field, newest first. Scan the day's progress in seconds.</p>

      {entries.length === 0 ? (
        <div className="mt-8 border-2 border-dashed border-[#2C3539]/20 p-10 text-center">
          <Camera size={32} className="mx-auto text-[#2F5F80]" />
          <div className="mt-3 font-display text-xl uppercase text-[#2C3539]">No field updates yet</div>
          <p className="text-sm text-[#2C3539]/60">When the crew uploads photos from a job site, they'll appear here instantly.</p>
        </div>
      ) : (
        <div className="mt-6 space-y-8">
          {entries.map((g) => (
            <div key={g.label} data-testid={`feed-day-${g.label.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>
              <div className="flex items-center gap-3">
                <div className="font-display text-lg uppercase text-[#2F5F80]">{g.label}</div>
                <div className="h-px flex-1 bg-[#2C3539]/10" />
                <span className="font-mono-av text-xs text-[#2C3539]/50">{g.items.length} update{g.items.length === 1 ? "" : "s"}</span>
              </div>
              <div className="mt-4 space-y-0 border-l-2 border-[#2C3539]/10 ml-2">
                {g.items.map((j) => (
                  <FeedEntry key={j.id} j={j} onOpenPhoto={onOpenPhoto} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function FeedEntry({ j, onOpenPhoto }) {
  return (
    <article className="relative pl-6 pb-6 last:pb-0" data-testid={`feed-entry-${j.id}`}>
      <span className="absolute -left-[7px] top-1.5 w-3 h-3 bg-[#2F5F80] border-2 border-white" />
      <div className="flex gap-4 flex-wrap sm:flex-nowrap">
        <button
          onClick={() => onOpenPhoto?.(j)}
          className="w-full sm:w-28 h-32 sm:h-20 shrink-0 bg-[#2C3539] overflow-hidden border-2 border-[#2C3539]/15 hover:border-[#2F5F80] transition-colors"
          data-testid={`feed-photo-${j.id}`}
        >
          <img src={jobPhotoSrc(j)} alt={j.job_site_name} className="w-full h-full object-cover" loading="lazy" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="font-display text-lg uppercase text-[#23282B]">{j.job_site_name}</span>
            <span className="text-xs text-[#23282B]/50">{timeOf(j.created_at)} · {(j.uploaded_by || "").split("@")[0]}</span>
          </div>
          <div className="mt-0.5 text-xs text-[#23282B]/60 flex flex-wrap gap-x-3 gap-y-0.5">
            <span className="text-[#2F5F80] font-semibold">{j.service_type}</span>
            {j.location && <span className="inline-flex items-center gap-1"><MapPin size={11} /> {j.location}</span>}
          </div>
          {(j.phase || (j.quick_tags || []).length > 0) && (
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {j.phase && <span className="bg-[#2F5F80] text-white text-[10px] font-bold uppercase tracking-[0.12em] px-2 py-0.5">{PHASE_LABEL[j.phase]}</span>}
              {(j.quick_tags || []).map((t) => (
                <span key={t} className="inline-flex items-center gap-1 bg-[#9CC4DB]/25 text-[#234B66] text-[10px] font-bold uppercase tracking-[0.1em] px-2 py-0.5">
                  <Tag size={9} /> {t}
                </span>
              ))}
            </div>
          )}
          {j.description && <p className="mt-1.5 text-sm text-[#23282B]/80">{j.description}</p>}
        </div>
      </div>
    </article>
  );
}
